import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Container, Typography, Box, CircularProgress, Alert, Button, Divider, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from '@mui/material';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

const PatientReportDetails = () => {
  const { id } = useParams();
  const [report, setReport] = useState(null);
  const [testResults, setTestResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchReport = async () => {
      setLoading(true);
      setError('');
      try {
        // Fetch both report details and test results using patient-specific endpoints
        const [reportResponse, resultsResponse] = await Promise.all([
          axios.get(`${API_URL}/api/patients/me/reports/${id}`),
          axios.get(`${API_URL}/api/patients/me/reports/${id}/results`)
        ]);
        setReport(reportResponse.data);
        setTestResults(resultsResponse.data);
      } catch (err) {
        console.error('Error fetching report:', err);
        setError(`Failed to load report: ${err.response?.data || err.message}`);
      } finally {
        setLoading(false);
      }
    };
    fetchReport();
  }, [id]);

  const handlePrint = () => {
    window.print();
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!report) return null;

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 700 }}>Report Details</Typography>
      
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Report Information</Typography>
        <Box sx={{ mb: 2 }}>
          <Typography><strong>Date:</strong> {formatDate(report.date)}</Typography>
          <Typography><strong>Status:</strong> {report.status}</Typography>
          <Typography><strong>Referring Doctor:</strong> {report.referringDoctor || 'N/A'}</Typography>
          <Typography><strong>Interpretation:</strong> {report.interpretation || 'N/A'}</Typography>
        </Box>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Test Results</Typography>
        {testResults && testResults.length > 0 ? (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Test Name</strong></TableCell>
                  <TableCell><strong>Value</strong></TableCell>
                  <TableCell><strong>Unit</strong></TableCell>
                  <TableCell><strong>Reference Range</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {testResults.map((result, index) => (
                  <TableRow key={index}>
                    <TableCell>{result.test.name}</TableCell>
                    <TableCell>{result.value}</TableCell>
                    <TableCell>{result.test.unit || 'N/A'}</TableCell>
                    <TableCell>{result.test.referenceRange || 'N/A'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography color="text.secondary">No test results available</Typography>
        )}
      </Paper>

      <Box sx={{ textAlign: 'center' }}>
        <Button variant="outlined" onClick={handlePrint} sx={{ mt: 2 }}>
          Print Report
        </Button>
      </Box>

      {/* Print-specific CSS */}
      <style>{`
        @media print {
          body * { visibility: hidden; }
          .MuiContainer-root, .MuiContainer-root * { visibility: visible; }
          .MuiContainer-root { position: absolute; left: 0; top: 0; width: 100vw; background: white; }
          button { display: none !important; }
        }
      `}</style>
    </Container>
  );
};

export default PatientReportDetails; 